import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type IAlert } from 'shared/ui/Alert/types'
import { AgentActions } from 'entities/Chatbot/model/AgentActions'
import { AgentErrors } from 'entities/Chatbot/model/AgentError'
import { type IAgentThirdStepPayload, type IChatbotFullData } from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { ChatbotAction } from 'pages/chatbot/store/chatbotActionStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'

type SectionUnion = 'dataCollection' | 'goalMet' | 'goalNotMet' | 'qualification'

export class AgentActionsStore {
  private _openedSections: Set<SectionUnion> = new Set(['dataCollection'])
  private _successActionsModel = new AgentActions()
  private _fallbackActionsModel = new AgentActions()
  private _successActionsErrors = new AgentErrors(this)
  private _fallbackActionsErrors = new AgentErrors(this)
  private _qualifiedActionsErrors = new AgentErrors(this)
  private _disqualifiedActionsErrors = new AgentErrors(this)
  private _disposeUpdateOpenedSections: IReactionDisposer | null = null

  private _successActions: ChatbotAction = new ChatbotAction(
    this._successActionsModel,
    this._successActionsErrors.successActions
  )
  private _fallbackActions: ChatbotAction = new ChatbotAction(
    this._fallbackActionsModel,
    this._fallbackActionsErrors.fallbackActions
  )
  private _loading = false

  constructor(
    private _stepsStore: AgentBookAppointmentStepsStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this.reactionUpdateOpenedSections()
  }

  reactionUpdateOpenedSections = () => {
    this._disposeUpdateOpenedSections?.()
    this._disposeUpdateOpenedSections = reaction(
      () => this.isChatBookingType,
      () => {
        this.handleBookingTypeChange()
      }
    )
  }

  init = (data: IChatbotFullData) => {
    this._successActionsModel.syncOrigin(data.success_actions)
    this._fallbackActionsModel.syncOrigin(data.fallback_actions)

    if (this.bookingType) {
      this.handleBookingTypeChange()
    }
  }

  get chatbot() {
    return this._stepsStore.chatbot
  }

  get isChatBookingType() {
    return this._stepsStore.connectCalendarStore.isChatBookingType
  }

  get bookingType() {
    return this._stepsStore.connectCalendarStore.bookingType
  }

  get successActions() {
    return this._successActions
  }

  get fallbackActions() {
    return this._fallbackActions
  }

  get successActionsModel() {
    return this._successActionsModel
  }

  get fallbackActionsModel() {
    return this._fallbackActionsModel
  }

  get successActionsErrors() {
    return this._successActionsErrors
  }

  get fallbackActionsErrors() {
    return this._fallbackActionsErrors
  }
  get qualifiedActionsErrors() {
    return this._qualifiedActionsErrors
  }
  get disqualifiedActionsErrors() {
    return this._disqualifiedActionsErrors
  }

  get stepPayload(): IAgentThirdStepPayload {
    return {
      success_actions: this._successActionsModel.toJSON(),
      fallback_actions: this._fallbackActionsModel.toJSON(),
      qualified_actions: this.chatbot.qualifiedActions.toJSON(),
      disqualified_actions: this.chatbot.disqualifiedActions.toJSON(),
      fields: this._stepsStore.chatbot.fields.list,
      settings: {
        skip_known_fields: { value: this._stepsStore.chatbot.settings.skipKnownFields },
        data_collection_instruction: {
          value: this._stepsStore.chatbot.settings.dataCollectionInstruction,
        },
        qualify_enabled: {
          value: this._stepsStore.chatbot.settings.qualifyEnabled,
        },
        qualify_instruction: {
          value: this._stepsStore.chatbot.settings.qualifyInstruction,
        },
        disqualify_behavior: {
          value: this._stepsStore.chatbot.settings.disqualifyBehavior,
        },
      },
    }
  }

  get dataCollectionErrorAlert(): IAlert | undefined {
    if (this._stepsStore.stepObjectiveStore.stepObjectiveBookingStore.errorAlert) {
      return this._stepsStore.stepObjectiveStore.stepObjectiveBookingStore.errorAlert
    }

    return
  }

  get dataCollectionError() {
    const errorMergeFieldsMinLimit = this._stepsStore.chatbotErrors.getError(
      'settings.data_collection_instruction.merge_fields_min_1'
    )
    const errorMergeFieldsMaxLimit = this._stepsStore.chatbotErrors.getError(
      'settings.data_collection_instruction.merge_fields_max_10'
    )

    if (errorMergeFieldsMinLimit) return errorMergeFieldsMinLimit
    if (errorMergeFieldsMaxLimit) return errorMergeFieldsMaxLimit

    return ''
  }

  get disabled() {
    return this._loading
  }

  onToggle = (section: SectionUnion) => {
    this.isOpened(section)
      ? this._openedSections.delete(section)
      : this._openedSections.add(section)
  }

  isOpened = (section: SectionUnion) => {
    return this._openedSections.has(section)
  }

  private _syncErrors = (errors: IHandleError) => {
    this._successActionsErrors.sync(errors)
    this._fallbackActionsErrors.sync(errors)
    this._qualifiedActionsErrors.sync(errors)
    this._disqualifiedActionsErrors.sync(errors)
    this._stepsStore.chatbotErrors.sync(errors)
  }

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      const isValidate = this._stepsStore.stepObjectiveStore.validateBooking()

      if (isValidate) return false

      await this._stepsStore.quickSaveDraft('step_3', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._stepsStore.handleApiError(e, this._syncErrors)

      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleNextClick = async (id?: string) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      const isValidate = this._stepsStore.stepObjectiveStore.validateBooking()

      if (isValidate) return false

      await this._stepsStore.handleStepValidationAndNavigation('step_3', this.stepPayload, id)
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this._syncErrors)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeUpdateOpenedSections?.()
  }

  handleBookingTypeChange = () => {
    const bookAppointmentLinkId = this._stepsStore.connectCalendarStore.eventLink?.data?.id
    if (bookAppointmentLinkId) {
      this._stepsStore.calendlyFieldsStore.fetchFields({
        params: bookAppointmentLinkId,
      })
    }
  }
}
